import {UILogger} from '@joyrideautos/ui-logger/src/Logger';

// Extend Window interface for grecaptcha
declare global {
    interface Window {
        grecaptcha: any;
    }
}

export class ReCaptchaService {
    private grecaptcha: any = null;
    private initializationPromise: Promise<void> | null = null;

    constructor(private siteKey: string | undefined, private disabled: boolean, private logger: UILogger) {}

    async initialize(): Promise<void> {
        // Return existing promise if initialization is already in progress
        if (this.initializationPromise) {
            return this.initializationPromise;
        }

        // If already initialized, return immediately
        if (this.grecaptcha) {
            return Promise.resolve();
        }

        if (!this.siteKey || this.disabled) {
            return Promise.resolve();
        }

        // Create and store the initialization promise to prevent concurrent initializations
        this.initializationPromise = (async () => {
            // Load reCAPTCHA v3 script
            if (!window.grecaptcha) {
                await this.loadRecaptchaScript();
                this.logger.log('reCAPTCHA script loaded');
            }

            this.grecaptcha = window.grecaptcha;
        })();

        try {
            await this.initializationPromise;
        } finally {
            // Clear the promise after completion (success or failure)
            // This allows retry on failure
            this.initializationPromise = null;
        }
    }

    private loadRecaptchaScript(): Promise<void> {
        return new Promise((resolve, reject) => {
            const script = document.createElement('script');
            script.src = `https://www.google.com/recaptcha/api.js?render=${this.siteKey}`;
            script.onload = () => resolve();
            script.onerror = () => reject(new Error('Failed to load reCAPTCHA'));
            document.head.appendChild(script);
        });
    }

    async getToken(action: string): Promise<string> {
        // Automatically initialize if not already done
        await this.initialize();

        if (!this.grecaptcha || !this.siteKey) {
            throw new Error('reCAPTCHA not initialized');
        }

        return new Promise((resolve, reject) => {
            this.grecaptcha.ready(() => {
                this.grecaptcha.execute(this.siteKey, {action}).then(resolve).catch(reject);
            });
        });
    }

    isAvailable(): boolean {
        return !this.disabled && !!this.siteKey;
    }

    isReady(): boolean {
        return !!this.grecaptcha && !this.initializationPromise;
    }
}
